import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import * as colors from 'src/styles/colors';
import { FaSync, FaVolumeUp } from 'react-icons/fa'; // 引入語音圖示
import { Api } from 'src/api/config';

const CaptchaDiv = styled.div<{ color?: string }>`
  display: flex;
  align-items: center;
  border: 1px solid ${(props) => (props.color ? props.color : colors.primaryBlue_100)};
  border-radius: 4px;
  height: 40px;
  width: max-content;
  min-width: 180px;
  overflow: hidden;
`;
const Img = styled.img<{ size?: string }>`
  height: ${(props) => (props.size ? props.size : '40px')};
  width: 100px;
`;
const Icon = styled.div<{ size?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.BlueGray_800};
  width: ${(props) => (props.size ? props.size : '40px')};
  cursor: pointer;
  border-left: 1px solid #eee;
  height: 100%;
  &:hover { background-color: #f5f5f5; }
  &:focus {
    outline: 2px solid;
    outline-offset: -2px;
  }
`;

interface IProps {
    size?: string;
    color?: string;
    captchaUuid: string;
    setCaptchaUuid: (captchaUuid: string) => void;
    captchaUriSuffix: number;
    setCaptchaUriSuffix: (captchaUriSuffix: number) => void;
}

const Captcha: React.FC<IProps> = ({
    size,
    color,
    captchaUuid,
    setCaptchaUuid,
    captchaUriSuffix,
    setCaptchaUriSuffix,
}) => {
    const [captchaUrl, setCaptchaUrl] = useState<string>('');
    
    const handleSpeak = useCallback(async () => {
        try {
            // 取得驗證碼純文字內容 (假設 API 路徑如下)
            const res = await Api().get('captcha/audioText?uuid=' + captchaUuid);
            const encodedText = res.data; // 取得驗證碼字串
            const decodedText = encodedText === '驗證碼過期' ? '驗證碼過期' : decodeURIComponent(escape(atob(encodedText)));; // 處理 UTF-8 中文解碼

            const synth = window.speechSynthesis;
            if (synth.speaking) synth.cancel();

            // 將字串拆開，讓朗讀更清晰 (例如: 1 2 3 4)
            const utterThis = new SpeechSynthesisUtterance();

            utterThis.lang = 'zh-TW';
            utterThis.rate = 0.8; // 稍微放慢一點讓使用者聽得清楚
			utterThis.text = decodedText === '驗證碼過期' ? '驗證碼過期' : decodedText.split('').join(',');

            synth.speak(utterThis);
        } catch (e) {
            console.error("語音播放失敗", e);
        }
    }, [captchaUuid]);
    // --- 語音功能邏輯結束 ---

    useEffect(() => {
        const getCaptchaUuid = async () => {
            const res = await Api().get('captcha/uuid');
            setCaptchaUuid(res.data);
        };
        getCaptchaUuid();
    }, [setCaptchaUuid]);

    useEffect(() => {
        captchaUuid &&
            setCaptchaUrl(
                process.env.REACT_APP_API_URL +
                'captcha/generate?uuid=' +
                captchaUuid +
                '&time=' +
                captchaUriSuffix
            );
    }, [captchaUuid, captchaUriSuffix]);

    const reloadCaptcha = () => {
        setCaptchaUriSuffix(Date.now());
    };

    // 處理按鈕的鍵盤觸發事件
    const handleKeyDown = (callback: () => void) => (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            callback();
        }
    };

    return (
        <CaptchaDiv color={color}>
            <Img id="captcha_img" src={captchaUrl} alt="驗證碼" size={size} />

            {/* 播放語音按鈕 */}
            <Icon size={size} title="播放驗證碼語音" onClick={handleSpeak} tabIndex={0} role="button" onKeyDown={handleKeyDown(handleSpeak)} aria-label="播放驗證碼語音" >
                <FaVolumeUp />
            </Icon>
            {/* 重新整理按鈕 */}
            <Icon size={size} title="重新產生驗證碼" onClick={reloadCaptcha} tabIndex={0} role="button" onKeyDown={handleKeyDown(reloadCaptcha)} aria-label="重新產生驗證碼" >
                <FaSync />
            </Icon>
        </CaptchaDiv>
    );
};

export default Captcha;